import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import useGameSounds from "../../hooks/useGameSounds";
import useKidVoice from "../../hooks/useKidVoice";
import useEntrySound from "../../hooks/useEntrySound";

const API = process.env.REACT_APP_API_URL;
const AVATARS = ["🦄", "🐯", "🐶", "🐱", "🦁", "🐼", "🐸", "🐵", "🦊", "🐰"];

export default function KidLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sounds    = useGameSounds();
  const { speak } = useKidVoice();
  useEntrySound("Who's playing today? Tap your picture!");

  const [family, setFamily]   = useState(null);   // { email, children: [...] }
  const [picked, setPicked]   = useState(null);   // selected child
  const [pin, setPin]         = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    try {
      const f = JSON.parse(localStorage.getItem("kids_family") || "null");
      if (f && f.children && f.children.length) setFamily(f);
    } catch { /* ignore */ }
  }, []);

  const choose = (child) => {
    sounds.playClick();
    setPicked(child);
    setPin("");
    setError("");
    if (!child.has_pin) doLogin(child, "");   // no PIN set → go straight in
  };

  const pressDigit = (d) => {
    if (pin.length >= 4) return;
    sounds.playTick();
    const next = pin + d;
    setPin(next);
    setError("");
  };

  const doLogin = async (child, pinValue) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/kids/child/login`, {
        email: family.email,
        child_id: child.id,
        pin: pinValue || undefined,
      });
      const { token, child: c } = res.data.data;

      sounds.playComplete(false);
      speak(`Hi ${c.name}! Let's play and learn!`);

      // Reuse the existing bearer-token key so the games hub / AI chat work unchanged
      localStorage.setItem("kids_parent_token", token);
      localStorage.setItem("kids_active_mode", "child");
      localStorage.setItem("kids_child", JSON.stringify(c));

      // Cache for instant first paint; the hub re-fetches fresh data by ID.
      sessionStorage.setItem("childId",     c.id);
      sessionStorage.setItem("childName",   c.name);
      sessionStorage.setItem("childGrade",  c.grade);
      sessionStorage.setItem("childXP",     c.total_xp);
      sessionStorage.setItem("childCoins",  c.coins);
      sessionStorage.setItem("childStreak", c.streak_days);

      // Only the ID travels in the URL — no name / XP / coins exposed.
      navigate(`/kids/games?childId=${c.id}`);
    } catch (err) {
      sounds.playWrong();
      setError(err.response?.data?.message || "Something went wrong. Try again.");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const avatarFor = (child, i) =>
    /^\p{Emoji}/u.test(child.avatar || "") ? child.avatar : AVATARS[i % AVATARS.length];

  /* ── No family cached on this device ── */
  if (!family) {
    return (
      <Shell>
        <div className="bg-white rounded-3xl shadow-sheet p-8 text-center max-w-sm w-full">
          <div className="text-6xl mb-3">🔒</div>
          <h1 className="font-display font-bold text-xl text-ink mb-2">Ask a grown-up first</h1>
          <p className="font-body text-muted text-sm mb-6">
            A parent needs to sign in on this device once. After that, you can jump straight in with your PIN!
          </p>
          <Link
            to="/kids/login"
            className="inline-flex items-center justify-center gap-2 w-full min-h-[44px] rounded-xl px-6
              bg-brand hover:bg-brand-hover text-white shadow-primary font-display font-semibold text-[15px]
              transition active:scale-[0.97]"
          >
            {t("kids.login.parentLogin")}
          </Link>
        </div>
      </Shell>
    );
  }

  /* ── PIN entry ── */
  if (picked && picked.has_pin) {
    return (
      <Shell>
        <div className="bg-white rounded-3xl shadow-sheet p-8 text-center max-w-sm w-full">
          <button
            onClick={() => { setPicked(null); setPin(""); setError(""); }}
            className="font-body font-extrabold text-faint hover:text-brand text-sm mb-3 block transition"
          >
            ← Pick someone else
          </button>
          <div className="text-6xl mb-1">{avatarFor(picked, 0)}</div>
          <h1 className="font-display font-bold text-2xl text-ink">Hi {picked.name}!</h1>
          <p className="font-body text-muted text-sm mb-5">{t("kids.login.enterPin")} 🔑</p>

          {/* PIN dots */}
          <div className="flex justify-center gap-2 mb-2">
            {[0,1,2,3].map(i => (
              <div key={i} className={`w-4 h-4 rounded-full transition ${
                i < pin.length ? "bg-brand" : "bg-orange-100"
              }`} />
            ))}
          </div>
          {error && <p className="font-body text-danger text-sm font-medium mb-2">{error}</p>}

          {/* Number pad */}
          <div aria-label={t("kids.login.pin")} className="grid grid-cols-3 gap-3 mt-4">
            {["1","2","3","4","5","6","7","8","9"].map(d => (
              <button key={d} onClick={() => pressDigit(d)} disabled={loading}
                className="min-h-[44px] bg-orange-50 hover:bg-orange-100 text-ink font-display font-extrabold text-2xl py-4 rounded-2xl transition active:scale-90">
                {d}
              </button>
            ))}
            <button onClick={() => setPin(p => p.slice(0, -1))} disabled={loading}
              className="min-h-[44px] text-faint hover:text-brand text-xl py-4 rounded-2xl transition">⌫</button>
            <button onClick={() => pressDigit("0")} disabled={loading}
              className="min-h-[44px] bg-orange-50 hover:bg-orange-100 text-ink font-display font-extrabold text-2xl py-4 rounded-2xl transition active:scale-90">0</button>
            <button aria-label={t("kids.login.go")} onClick={() => doLogin(picked, pin)} disabled={loading || pin.length < 4}
              className="min-h-[44px] bg-brand hover:bg-brand-hover disabled:opacity-40 text-white text-xl font-display font-extrabold py-4 rounded-2xl transition active:scale-90">
              {loading ? "…" : "✓"}
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  /* ── Profile picker ── */
  return (
    <Shell>
      <div className="text-center max-w-md w-full">
        <h1 className="font-display font-bold text-3xl text-ink mb-1">{t("kids.login.selectChild")} 🎮</h1>
        <p className="font-body text-muted text-sm mb-8">Tap your picture to start your adventure!</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {family.children.map((c, i) => (
            <button
              key={c.id}
              onClick={() => choose(c)}
              disabled={loading}
              style={{ boxShadow: "0 12px 24px -14px rgba(70,45,20,.4)" }}
              className="flex flex-col items-center gap-2 bg-white rounded-3xl p-4 border-2 border-transparent
                hover:border-brand transition active:scale-95"
            >
              <div
                className="w-[70px] h-[70px] rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(150deg,#FFE0B8,#FFC48A)",
                  boxShadow: "inset 0 0 0 3px #fff",
                  fontSize: 38,
                }}
              >
                {avatarFor(c, i)}
              </div>
              <span className="font-display font-semibold text-ink text-sm truncate w-full">{c.name}</span>
              {c.has_pin
                ? <span className="font-body font-extrabold text-[11px] text-muted bg-[#F1EAE1] px-3 py-1 rounded-full">🔒 PIN</span>
                : <span className="font-body font-extrabold text-[11px] text-success bg-[#E7F6EC] px-3 py-1 rounded-full">● Ready!</span>}
            </button>
          ))}
        </div>

        {error && <p className="font-body text-danger text-sm font-medium mt-4">{error}</p>}

        <Link to="/kids/login" className="inline-block mt-8 font-body font-semibold text-faint hover:text-brand text-sm transition">
          Not you? {t("kids.login.parentLogin")} →
        </Link>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 font-body"
      style={{ background: "linear-gradient(160deg, #FFF8F2 0%, #FFE9D6 45%, #FFD9BF 100%)" }}>
      <Link to="/kids" className="mb-6 font-display font-bold text-brand text-2xl tracking-tight">
        ⭐ Cabil <span className="text-gold">Kids</span>
      </Link>
      {children}
    </div>
  );
}
